import { useEffect, useRef, useState } from "react"
import timestampService from "../../service/timestampService"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import labels from "../../utils/labels"
import TimePicker from "../UI/input/TimePicker"
import LabeledSection from "../UI/LabeledSection"
import EmployeeSettingsService from "../../service/employeeSettingsService"
import { clearEmployeeWorkTimeEdit, editEmployeeWorkTime } from "../../store/employeeSettings"
import StandardButton from "../UI/buttons/StandardButton"
import { EmployeeWorkTime } from "../../types/ganttChart"
import EmployeeSettingsStyles from "../../styles/employeeSettings.module.css"

const EmployeeWorktime = ({
  onUnsavedEdit
}: {
  onUnsavedEdit: (hasChanges: boolean) => void
}) => {
  const { employeeWorkTime, employeeShiftId, manualShiftChanges }
    = useAppSelector((state) => state.drawerSlice.selectedEmployee!)
  const { manualEdits } = useAppSelector((state) => state.employeeSettingsSlice)
  const dispatch = useAppDispatch()
  const [localWorkTime, setLocalWorkTime] = useState<EmployeeWorkTime>({ workTimeStart: "None", workTimeEnd: "None" })
  const initialWorkTimeRef = useRef<EmployeeWorkTime>({ workTimeStart: "None", workTimeEnd: "None" })
  const [isPending, setIsPending] = useState(false)

  const manualWorkTimeEdits =
    manualEdits[employeeShiftId]?.employeeWorkTime
    ?? manualShiftChanges?.employeeWorkTime
  const timeToUse = manualWorkTimeEdits ?? employeeWorkTime

  useEffect(() => {
    if (!timeToUse) return;
    const mappedTime: EmployeeWorkTime = {
      workTimeStart: timestampService.getHoursAndMinutesTimestamp(
        timeToUse.workTimeStart
      ),
      workTimeEnd: timestampService.getHoursAndMinutesTimestamp(
        timeToUse.workTimeEnd
      )
    }
    setLocalWorkTime(mappedTime)
    initialWorkTimeRef.current = mappedTime
  }, [employeeShiftId, timeToUse])

  function onInputChange(time: "start" | "end", value: string) {
    const newTimeWindow = {
      workTimeStart: time === "start" ? value : localWorkTime.workTimeStart,
      workTimeEnd: time === "end" ? value : localWorkTime.workTimeEnd,
    }
    setLocalWorkTime(newTimeWindow)
  }

  const hasChanges =
    localWorkTime.workTimeStart !== initialWorkTimeRef.current.workTimeStart ||
    localWorkTime.workTimeEnd !== initialWorkTimeRef.current.workTimeEnd

  useEffect(() => {
    onUnsavedEdit(hasChanges)
  }, [onUnsavedEdit, hasChanges])

  async function handleTimeWindowUpdate() {
    const updatedStart = timestampService.mergeTimeIntoDate(
      timeToUse.workTimeStart,
      localWorkTime.workTimeStart
    )
    const updatedEnd = timestampService.mergeTimeIntoDate(
      timeToUse.workTimeEnd,
      localWorkTime.workTimeEnd
    )

    const matchesBaseWorkTime =
      updatedStart === employeeWorkTime.workTimeStart &&
      updatedEnd === employeeWorkTime.workTimeEnd

    setIsPending(true)
    try {
      await EmployeeSettingsService.editEmployeeWorkTime(employeeShiftId, updatedStart, updatedEnd, dispatch)
      dispatch(editEmployeeWorkTime({
        shiftId: employeeShiftId,
        workTimeStart: updatedStart,
        workTimeEnd: updatedEnd
      }))
      // if the edit is back to original worktime - clear that from the store
      if (matchesBaseWorkTime) dispatch(clearEmployeeWorkTimeEdit({ shiftId: employeeShiftId }))
      initialWorkTimeRef.current = localWorkTime
      setIsPending(false)
    } catch {
      setLocalWorkTime(initialWorkTimeRef.current)
      setIsPending(false)
    }
  }

  const handleDiscardWorkTimeChanges = () => {
    setLocalWorkTime(initialWorkTimeRef.current)
  }

  return (
    <LabeledSection title={labels.editWorkingHours} titleSize="h4" spacing="small">
      <div className="flex flex-col w-full gap-[var(--spacing-md)]">
        <div className={
          `flex gap-[var(--spacing-md)] 
          ${manualWorkTimeEdits && EmployeeSettingsStyles.editedSetting}`
        }>
          <TimePicker
            id="employee-start-time"
            value={localWorkTime.workTimeStart}
            onTimeChange={(newStart) => { onInputChange("start", newStart) }}
            label={labels.startTime}
            classes="w-full"
            helperText={manualWorkTimeEdits && labels.manuallyEditedWorkTime}
          />
          <TimePicker
            id="employee-end-time"
            value={localWorkTime.workTimeEnd}
            onTimeChange={(newEnd) => { onInputChange("end", newEnd) }}
            label={labels.endTime}
            classes="w-full"
            helperText={manualWorkTimeEdits && labels.manuallyEditedWorkTime}
          />
        </div>
        {(hasChanges || isPending) &&
          <div className="flex gap-[var(--spacing-sm)] w-full justify-end">
            <StandardButton
              variant="SECONDARY"
              classes="w-fit"
              onClick={handleDiscardWorkTimeChanges}
            >
              {labels.discard}
            </StandardButton>

            <StandardButton
              variant="PRIMARY"
              classes="w-[84px]"
              onClick={handleTimeWindowUpdate}
              pending={isPending}
            >
              {labels.save}
            </StandardButton>
          </div>
        }
      </div>
    </LabeledSection>
  )
}

export default EmployeeWorktime